import {
  type JSX,
  type Ref,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";

import { componentManager, rwc } from "@frontend/bridge/ComponentManager";
import { getElementInternals } from "@frontend/bridge/elementInternals";
import { useIslandHost } from "@frontend/contexts";

import { Select } from "./Select";
import {
  type SelectDataProps,
  type SelectOption,
  selectDataSchema,
} from "./Select.schema";
import { SelectContent } from "./SelectContent";
import { SelectGroup } from "./SelectGroup";
import { SelectGroupLabel } from "./SelectGroupLabel";
import { SelectItem } from "./SelectItem";
import { SelectTrigger } from "./SelectTrigger";
import { SelectValue } from "./SelectValue";
import {
  SELECT_GROUP_TAG_NAME,
  SELECT_ITEM_TAG_NAME,
  SELECT_TAG_NAME,
} from "./constants";

export interface SelectHandle {
  select: (id: string | null) => void;
  update: (items: SelectOption[], selectedId?: string | null) => void;
  empty: () => void;
  disable: () => void;
  enable: () => void;
}

declare module "@frontend/bridge/types" {
  interface ComponentRegistryMap {
    [SELECT_TAG_NAME]: { props: SelectDataProps; handle: SelectHandle };
  }
  interface InertTagNames {
    [SELECT_ITEM_TAG_NAME]: true;
    [SELECT_GROUP_TAG_NAME]: true;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rwc-select": HTMLElement & SelectHandle;
  }
}

const RWCSelectItem = rwc(SELECT_ITEM_TAG_NAME);

const RWCSelectGroup = rwc(SELECT_GROUP_TAG_NAME);

function readSelectItems(scope: ParentNode, group?: string): SelectOption[] {
  return Array.from(
    scope.querySelectorAll<HTMLElement>(`:scope > ${RWCSelectItem.tagName}`),
  )
    .filter((item) => item.getAttribute("value") !== "")
    .map((item) => ({
      id: item.getAttribute("value") ?? "",
      text: item.textContent?.trim() ?? "",
      ...(group !== undefined ? { group } : {}),
      ...item.dataset,
    }));
}

export function readSelectSlotted(el: HTMLElement): Partial<SelectDataProps> {
  const groupEls = Array.from(
    el.querySelectorAll<HTMLElement>(`:scope > ${RWCSelectGroup.tagName}`),
  );

  const items: SelectOption[] = [
    ...readSelectItems(el),
    ...groupEls.flatMap((groupEl) =>
      readSelectItems(groupEl, groupEl.getAttribute("label") ?? ""),
    ),
  ];

  const itemEls = el.querySelectorAll<HTMLElement>(
    [
      `:scope > ${RWCSelectItem.tagName}`,
      `:scope > ${RWCSelectGroup.tagName} > ${RWCSelectItem.tagName}`,
    ].join(", "),
  );
  const selectedItem = Array.from(itemEls).find((item) =>
    item.hasAttribute("selected"),
  );

  const defaultValue =
    el.getAttribute("value") ??
    el.dataset.initialId ??
    selectedItem?.getAttribute("value") ??
    null;

  if (defaultValue) {
    el.setAttribute("value", defaultValue);
  }

  return {
    items,
    defaultValue,
    onValueChange: (id, item) => {
      if (id) {
        el.setAttribute("value", id);
      } else {
        el.removeAttribute("value");
      }

      el.dispatchEvent(
        new CustomEvent("select:change", {
          detail: { id, item },
          bubbles: true,
        }),
      );
    },
  };
}

interface SelectOptionGroup {
  value: string;
  items: SelectOption[];
}

function groupSelectItems(items: SelectOption[]): SelectOptionGroup[] {
  const groups = new Map<string, SelectOption[]>();

  for (const item of items) {
    const key = item.group ?? "";
    const bucket = groups.get(key);

    if (bucket) {
      bucket.push(item);
    } else {
      groups.set(key, [item]);
    }
  }

  return Array.from(groups, ([value, groupItems]) => ({
    value,
    items: groupItems,
  }));
}

function RWCSelectComponent({
  ref,
  items: initialItems = [],
  defaultValue,
  disabled: initialDisabled,
  placeholder = "…",
  variant,
  layout,
  size,
  required,
  name,
  alignItemWithTrigger,
  popupWidth,
  onValueChange,
}: Partial<SelectDataProps> & { ref?: Ref<SelectHandle> }): JSX.Element {
  const host = useIslandHost();

  const [items, setItems] = useState(initialItems);
  const [value, setValue] = useState(defaultValue ?? null);
  const [disabled, setDisabled] = useState(initialDisabled ?? false);

  useEffect(() => {
    getElementInternals(host)?.setFormValue(disabled ? null : value);
  }, [host, value, disabled]);

  useImperativeHandle(
    ref,
    () => ({
      select: (id) => setValue(id),
      update: (nextItems, selectedId = null) => {
        setItems(nextItems);
        setValue(selectedId);
      },
      empty: () => {
        setItems([]);
        setValue(null);
      },
      disable: () => setDisabled(true),
      enable: () => setDisabled(false),
    }),
    [],
  );

  function handleValueChange(nextId: string | null) {
    const item = items.find((option) => option.id === nextId) ?? null;

    setValue(nextId);
    onValueChange?.(nextId, item);

    getElementInternals(host)?.setFormValue(disabled ? null : nextId);
  }

  const groupedItems = useMemo(() => {
    const isGrouped = items.some((item) => item.group !== undefined);

    return isGrouped ? groupSelectItems(items) : null;
  }, [items]);

  return (
    <Select<string>
      items={items.map((item) => ({ label: item.text, value: item.id }))}
      value={value}
      onValueChange={handleValueChange}
      disabled={disabled}
      required={required}
      name={name}
      size={size}
      layout={layout}
    >
      <SelectTrigger variant={variant}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>

      <SelectContent
        alignItemWithTrigger={alignItemWithTrigger}
        popupWidth={popupWidth}
      >
        {groupedItems
          ? groupedItems.map((group) => (
              <SelectGroup key={group.value}>
                {group.value ? (
                  <SelectGroupLabel>{group.value}</SelectGroupLabel>
                ) : null}
                {group.items.map((item) => (
                  <SelectItem key={item.id} value={item.id}>
                    {item.text}
                  </SelectItem>
                ))}
              </SelectGroup>
            ))
          : items.map((item) => (
              <SelectItem key={item.id} value={item.id}>
                {item.text}
              </SelectItem>
            ))}
      </SelectContent>
    </Select>
  );
}

componentManager.register(
  RWCSelectItem,
  RWCSelectGroup,
  rwc(SELECT_TAG_NAME, {
    component: RWCSelectComponent,
    dataSchema: selectDataSchema,
    usesSlots: true,
    readSlotted: readSelectSlotted,
    formAssociated: true,
  }),
);
